"use client";

import { IconDatabaseOutline18, IconPlusOutline18, IconTableOutline18 } from "nucleo-ui-outline-18";
import { PageLayout } from "@/components/ui/page-layout";
import { Button } from "@/components/ui/button";
import { SkeletonReveal } from "@/components/ui/skeleton-reveal";
import { useOrgTables } from "@/hooks/useOrgDatabase";
import Link from "next/link";
import { useState } from "react";
import { CreateTableModal } from "./CreateTableModal";
import { TablesListSkeleton } from "./DatabaseSkeletons";

export default function DatabasesClient() {
  const { data: tables, isLoading, error } = useOrgTables();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const hasTables = Boolean(tables?.length);

  return (
    <PageLayout header={{ useCommandMenu: false }}>
      <div className="mb-8 flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-ds-text-primary text-3xl font-bold tracking-tight">Database</h1>
          <p className="body-sm">Create tables, insert data, and query your organization&apos;s database.</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <span className="flex items-center gap-2 text-xs">
            <IconPlusOutline18 className="h-3 w-3" />
            Create Table
          </span>
        </Button>
      </div>
      <div className="border-ds-border-default shadow-ds-soft bg-ds-surface-card rounded-xl border">
        {error && (
          <div className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load tables: {error.message}</div>
        )}

        {!error && (isLoading || hasTables) && (
          <SkeletonReveal loading={isLoading} skeleton={<TablesListSkeleton />}>
            {hasTables && (
              <div className="divide-ds-neutral-200 dark:divide-ds-neutral-800 divide-y">
                {tables?.map(table => (
                  <Link
                    key={table.name}
                    href={`/databases/${encodeURIComponent(table.name)}`}
                    className="dark:hover:bg-ds-neutral-800/50 hover:bg-ds-surface-hover flex items-center gap-3 px-6 py-4 transition-colors"
                  >
                    <IconTableOutline18 className="text-ds-text-disabled h-4 w-4" />
                    <span className="body-sm-medium font-mono">{table.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </SkeletonReveal>
        )}

        {!isLoading && !error && tables && tables.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <IconDatabaseOutline18 className="dark:text-ds-text-subtle mb-3 h-10 w-10 text-zinc-300" />
            <p className="body-sm-medium mb-1">No tables yet</p>
            <p className="text-ds-text-muted mb-4 text-sm">Create your first table to start storing data.</p>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <span className="flex items-center gap-2 text-xs">
                <IconPlusOutline18 className="h-3 w-3" />
                Create Table
              </span>
            </Button>
          </div>
        )}
      </div>
      <CreateTableModal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} />
    </PageLayout>
  );
}
